import classJoin from 'classjoin';
import {h} from 'preact';
import {setVisualMode} from './Store/creators/common';
import {setFilterVisibility} from './Store/creators/visualData';
import {dispatch, SubscribedComponent} from './Store/index';
import State, {
	StateFilter,
	StateVisualBuilding,
	StateVisualFloor,
	StateVisualOverview,
	StateVisualSection,
	StateVisualStep,
} from './Store/State';
import updateAddress from './utils/updateAddress';
import visualStateToQueryString from './utils/visualStateToQueryString';
import VisualFloor from './VisualFloor';
import VisualOverview from './VisualOverview';
import VisualSection from './VisualSection';
import VisualTooltip from './VisualTooltip';

/**
 * Component Properties.
 */
interface VisualProps
{
	[key: string]: void;
}

/**
 * Component State.
 */
interface VisualState
{
	/** Состояние общего экрана */
	overview: StateVisualOverview;
	/** Состояние экрана корпуса */
	building: StateVisualBuilding;
	/** Состояние экрана секции */
	section: StateVisualSection;
	/** Состояние экрана этажа */
	floor: StateVisualFloor;
	/** Шаг визуального режима */
	step: StateVisualStep;
	/** Фильтр показан? */
	filterVisible: boolean;
	/** Внесены ли ручные изменения в параметры фильтра? */
	filterChanged: boolean;
}

/**
 * Визуальный режим.
 */
class Visual extends SubscribedComponent<State, VisualProps, VisualState>
{
	/**
	 * Проверяет, внесены ли ручные изменения в параметры фильтра.
	 * 
	 * @param filter Параметры фильтра
	 */
	protected static isFilterChanged( filter: StateFilter ): boolean
	{
		let changed: boolean = (
			( filter.squareTotal.from !== filter.squareTotal.min )
			|| ( filter.squareTotal.to !== filter.squareTotal.max )
			|| ( filter.totalCost.from !== filter.totalCost.min )
			|| ( filter.totalCost.to !== filter.totalCost.max )
		);
		
		if ( !changed )
		{
			for ( const checkbox of filter.roomType )
			{
				if ( checkbox.checked )
				{
					changed = true;
					break;
				}
			}
		}
		
		return changed;
	}
	
	/**
	 * Render component.
	 */
	public render(
		_props: VisualProps,
		{
			overview, building, section, floor, step, filterVisible,
			filterChanged,
		}: VisualState,
	): JSX.Element
	{
		let stepElement: JSX.Element;
		
		switch ( step )
		{
			case StateVisualStep.SECTION:
				stepElement = (
					<VisualSection
						building={building.building}
						{...section}
					/>
				);
				break;
			
			case StateVisualStep.FLOOR:
				stepElement = (
					<VisualFloor
						building={building.building}
						section={section.section}
						background={section.image}
						floorsCount={section.map.length}
						{...floor}
					/>
				);
				break;
			
			default:
				stepElement = (
					<VisualOverview
						{...overview}
					/>
				);
				break;
		}
		
		return (
			<div class="visual">
				<div class="controls">
					<button
						type="button"
						class="view-list"
						onClick={this.onViewListClick}
					>
						Квартиры списком
					</button>
					{
						( step === StateVisualStep.FLOOR )
						? ''
						: (
							<button
								type="button"
								class={
									classJoin(
										{
											changed: ( filterChanged && !filterVisible ),
										},
										['toggle-filter'],
									)
								}
								onClick={this.onToggleFilterClick}
							>
								{
									filterVisible
									? (
										<svg role="img"
											title="Скрыть фильтр"
											width="22"
											height="22"
										>
											<use xlinkHref="/images/icons.svg#close" />
										</svg>
									)
									: (
										<svg role="img"
											title="Показать фильтр"
											width="28"
											height="28"
										>
											<use xlinkHref="/images/icons.svg#options" />
										</svg>
									)
								}
							</button>
						)
					}
				</div>
				{stepElement}
				<VisualTooltip />
			</div>
		);
	}
	
	/**
	 * Handle Store changes.
	 */
	protected storeStateChanged( state: State ): void
	{
		const {
			visualData: {
				overview, building, section, floor, step, filterVisible,
			},
			filter,
		} = state;
		
		const filterChanged = Visual.isFilterChanged( filter );
		
		if (
			( overview === this.state.overview )
			&& ( building === this.state.building )
			&& ( section === this.state.section )
			&& ( floor === this.state.floor )
			&& ( step === this.state.step )
			&& ( filterVisible === this.state.filterVisible )
			&& ( filterChanged === this.state.filterChanged )
		)
		{
			return;
		}
		
		this.setState(
			{
				overview, building, section, floor, step, filterVisible,
				filterChanged,
			},
		);
		
		const query = visualStateToQueryString(
			filter,
			state.visualData,
		);
		updateAddress( query );
	}
	
	/**
	 * При нажатии на «Квартиры списком».
	 */
	protected onViewListClick = (): void =>
	{
		dispatch( setVisualMode( false ) );
	}
	
	/**
	 * При нажатии на «Показать/Скрыть фильтр».
	 */
	protected onToggleFilterClick = ( event: Event ): void =>
	{
		dispatch( setFilterVisibility( !this.state.filterVisible ) );
		
		(event.currentTarget as HTMLElement).blur();
	}
}

/**
 * Module.
 */
export {
	Visual as default,
	VisualProps,
	VisualState,
};



// WEBPACK FOOTER //
// ./app/Routes/Choose/App/Visual.tsx